// Re-acquire microphone + rebuild capture graph after device loss.
// See docs/planning/deepgram_streaming_robustness.md §3 Phase 3 (device change).

import { AUDIO_DEVICE_CHANGED_STATUS, type AudioDeviceLossReason } from "./deepgramDeviceMonitor";

export type DeepgramAudioRecoveryDeps = {
  isRunning: () => boolean;
  isRecovering: () => boolean;
  setRecovering: (value: boolean) => void;
  onStatus?: (status: string) => void;
  log: (...args: unknown[]) => void;
  teardownAudioGraph: () => void;
  wireAudioGraphFromStream: (stream: MediaStream) => Promise<void>;
  isWsOpen: () => boolean;
  getUserMedia?: () => Promise<MediaStream>;
};

export async function recoverDeepgramAudioAfterDeviceLoss(
  deps: DeepgramAudioRecoveryDeps,
  reason: AudioDeviceLossReason,
): Promise<void> {
  if (!deps.isRunning() || deps.isRecovering()) return;

  deps.setRecovering(true);
  deps.onStatus?.(AUDIO_DEVICE_CHANGED_STATUS);
  deps.log("[deepgram] recovering audio after device loss", reason);

  try {
    deps.teardownAudioGraph();
    const getUserMedia = deps.getUserMedia ?? (() => navigator.mediaDevices.getUserMedia({ audio: true }));
    const stream = await getUserMedia();
    await deps.wireAudioGraphFromStream(stream);
    if (deps.isRunning() && deps.isWsOpen()) {
      deps.onStatus?.("Listening…");
    }
  } catch (err) {
    deps.log("[deepgram] audio re-acquire failed", err);
  } finally {
    deps.setRecovering(false);
  }
}
